import {Extension} from "@tiptap/core";
import type {Editor} from "@tiptap/core";
import type {Slice} from "@tiptap/pm/model";
import {NodeSelection, Plugin, PluginKey, TextSelection} from "@tiptap/pm/state";
import {dropPoint} from "@tiptap/pm/transform";
import {Decoration, DecorationSet} from "@tiptap/pm/view";
import type {EditorView} from "@tiptap/pm/view";
import {findPrintoutBlockById} from "../../blocks/blockRegistry";
import {
    createPrintoutBlockInsertSlice,
    insertPrintoutBlockAtCoords,
} from "../printoutBlockInsert";
import {PrintoutDropCursor} from "./PrintoutDropCursor";
import {isPrintoutBlockSlice, resolvePrintoutBlockDropTarget} from "./printoutDropPosition";
import type {PrintoutDropTarget} from "./printoutDropPosition";
import {PRINTOUT_COLUMN_DROP_ATTRIBUTE, PRINTOUT_DRAG_ATTRIBUTE} from "./printoutDragStyles";

export const PRINTOUT_BLOCK_DRAG_TYPE = "application/x-crqlar-printout-block";

interface PrintoutDragAndDropStorage {
    paletteBlockId: string | null;
}

interface PrintoutDragState {
    active: boolean;
    column: number | null;
}

const printoutDragKey = new PluginKey<PrintoutDragState>("printoutDragAndDrop");
const IDLE_DRAG: PrintoutDragState = {active: false, column: null};

const setDragState = (view: EditorView, next: PrintoutDragState) => {
    const current = printoutDragKey.getState(view.state);

    if (current?.active === next.active && current.column === next.column) return;

    // Feedback changes neither the selection nor the saved document or undo history.
    view.dispatch(view.state.tr.setMeta(printoutDragKey, next).setMeta("addToHistory", false));
};

declare module "@tiptap/core" {
    interface Storage {
        printoutDragAndDrop: PrintoutDragAndDropStorage;
    }
}

// The palette owns dragstart, where browser payloads are writable/readable. Keep hover data on its
// editor instead of sharing module globals between documents. Drop always reads the real payload.
export const startPrintoutPaletteBlockDrag = (editor: Editor, dataTransfer: DataTransfer, blockId: string) => {
    if (editor.isDestroyed || !editor.isEditable) return;

    dataTransfer.setData(PRINTOUT_BLOCK_DRAG_TYPE, blockId);
    dataTransfer.effectAllowed = "copy";
    editor.storage.printoutDragAndDrop.paletteBlockId = blockId;
    setDragState(editor.view, {active: true, column: null});
};

export const endPrintoutDrag = (editor: Editor | null) => {
    if (!editor || editor.isDestroyed) return;

    editor.storage.printoutDragAndDrop.paletteBlockId = null;
    setDragState(editor.view, IDLE_DRAG);
};

const applyPrintoutBlockDrop = (view: EditorView, slice: Slice, target: PrintoutDropTarget, moved: boolean) => {
    const {selection} = view.state;

    // Dropping a move back on its own range is a no-op; copying beside that range remains valid.
    if (moved && target.from >= selection.from && target.from <= selection.to) return;

    const tr = view.state.tr;

    if (moved) tr.deleteSelection();

    const destination = tr.mapping.map(target.from);
    const replaceTo = tr.mapping.map(target.to);
    const $destination = tr.doc.resolve(destination);
    const $replaceTo = tr.doc.resolve(replaceTo);

    // Validate after deletion too. A rejected drop must never dispatch just the source deletion.
    if (!$destination.sameParent($replaceTo)
        || !$destination.parent.canReplace($destination.index(), $replaceTo.index(), slice.content)) return;

    tr.replaceWith(destination, replaceTo, slice.content);

    if (tr.doc.eq(view.state.doc)) return;

    const node = slice.content.firstChild!;
    tr.setSelection(slice.content.childCount === 1 && NodeSelection.isSelectable(node)
        ? NodeSelection.create(tr.doc, destination)
        : TextSelection.between(tr.doc.resolve(destination), tr.doc.resolve(destination + slice.size)));

    view.focus();
    view.dispatch(tr.setMeta("uiEvent", "drop"));
};

export const PrintoutDragAndDrop = Extension.create<{}, PrintoutDragAndDropStorage>({
    name: "printoutDragAndDrop",

    addStorage() {
        return {paletteBlockId: null};
    },

    addProseMirrorPlugins() {
        const editor = this.editor;
        const storage = this.storage;
        let cursor: PrintoutDropCursor | null = null;
        let removalTimeout = 0;

        const resolveCursorTarget = (view: EditorView, event: DragEvent): PrintoutDropTarget | null => {
            if (!view.editable) return null;

            const coords = {left: event.clientX, top: event.clientY};

            if (event.dataTransfer?.types.includes(PRINTOUT_BLOCK_DRAG_TYPE)) {
                const block = storage.paletteBlockId
                    ? findPrintoutBlockById(storage.paletteBlockId)
                    : undefined;
                const node = block?.createNode(view.state.schema);

                return node ? resolvePrintoutBlockDropTarget(view, createPrintoutBlockInsertSlice(node), coords) : null;
            }

            const slice = view.dragging?.slice;

            if (slice && isPrintoutBlockSlice(slice)) return resolvePrintoutBlockDropTarget(view, slice, coords);

            // Text and foreign drags are still dropped by ProseMirror, so preview its own rule.
            const hit = view.posAtCoords(coords);

            if (!hit) return null;

            const position = (slice && dropPoint(view.state.doc, hit.pos, slice)) ?? hit.pos;

            return {from: position, to: position, column: null};
        };

        const clearCursor = () => {
            cursor?.setPosition(null);
            window.clearTimeout(removalTimeout);
        };

        const clearHover = (view: EditorView) => {
            clearCursor();
            const current = printoutDragKey.getState(view.state);

            if (current) setDragState(view, {...current, column: null});
        };

        return [new Plugin<PrintoutDragState>({
            key: printoutDragKey,
            state: {
                init: () => IDLE_DRAG,
                apply: (tr, current) => tr.getMeta(printoutDragKey)
                    ?? (tr.docChanged ? {...current, column: null} : current),
            },
            props: {
                attributes: (state): Record<string, string> => (
                    printoutDragKey.getState(state)?.active ? {[PRINTOUT_DRAG_ATTRIBUTE]: "true"} : {}
                ),
                decorations: state => {
                    const drag = printoutDragKey.getState(state);
                    const column = drag?.column;

                    if (!drag?.active || column == null) return DecorationSet.empty;

                    const node = state.doc.nodeAt(column);

                    return node?.type.name === "printoutContainerColumn"
                        ? DecorationSet.create(state.doc, [Decoration.node(column, column + node.nodeSize, {
                            [PRINTOUT_COLUMN_DROP_ATTRIBUTE]: "true",
                        })])
                        : DecorationSet.empty;
                },
                handleDOMEvents: {
                    dragstart: (view, event) => {
                        const handle = event.target instanceof Element ? event.target.closest("[data-drag-handle]") : null;

                        if (view.editable && handle && view.dom.contains(handle) && handle.closest("[data-printout-block-type]")) {
                            setDragState(view, {active: true, column: null});
                        }

                        return false;
                    },
                    dragover: (view, event) => {
                        if (!view.editable) return false;

                        const target = resolveCursorTarget(view, event);
                        const blockDrag = !!storage.paletteBlockId
                            || !!(view.dragging && isPrintoutBlockSlice(view.dragging.slice));

                        setDragState(view, {active: blockDrag, column: target?.column ?? null});
                        cursor?.setPosition(target?.from ?? null);
                        window.clearTimeout(removalTimeout);
                        removalTimeout = window.setTimeout(() => clearHover(view), 5000);
                        return false;
                    },
                    dragleave: (view, event) => {
                        if (!(event.relatedTarget instanceof Node) || !view.dom.contains(event.relatedTarget)) clearHover(view);
                        return false;
                    },
                },
                handleDrop: (view, event, slice, moved) => {
                    if (!view.editable) return false;

                    const blockId = event.dataTransfer?.getData(PRINTOUT_BLOCK_DRAG_TYPE);

                    if (blockId) {
                        const block = findPrintoutBlockById(blockId);

                        if (block) insertPrintoutBlockAtCoords(editor, block, {left: event.clientX, top: event.clientY});
                        return true;
                    }

                    if (!view.dragging || !isPrintoutBlockSlice(slice)) return false;

                    const target = resolvePrintoutBlockDropTarget(view, slice, {left: event.clientX, top: event.clientY});

                    if (target) applyPrintoutBlockDrop(view, slice, target, moved);

                    // Consume rejected block drops too, so native insertion cannot bypass the rules.
                    return true;
                },
            },
            view: view => {
                cursor = new PrintoutDropCursor(view);
                const endDrag = () => {
                    clearCursor();
                    endPrintoutDrag(editor);
                };
                const onEscape = (event: KeyboardEvent) => {
                    if (event.key === "Escape") endDrag();
                };

                window.addEventListener("dragend", endDrag);
                window.addEventListener("drop", endDrag);
                window.addEventListener("keydown", onEscape);

                return {
                    update: (nextView, previousState) => {
                        // A changed document invalidates measured gaps; the next dragover measures again.
                        if (nextView.state.doc !== previousState.doc) clearCursor();
                    },
                    destroy: () => {
                        window.removeEventListener("dragend", endDrag);
                        window.removeEventListener("drop", endDrag);
                        window.removeEventListener("keydown", onEscape);
                        storage.paletteBlockId = null;
                        clearCursor();
                        cursor = null;
                    },
                };
            },
        })];
    },
});
